import ScrollDesktop from "layout/ScrollFeature/ScrollDesktop";
import { JSXElementConstructor, ReactElement, RefObject, cloneElement, createContext, useContext, useEffect, useRef, useState } from "react";

interface ScrollContextType {
    children: ReactElement<any, string | JSXElementConstructor<any>>[];
    activeSection: string | null;
    mobileFooterButonRefs: Record<string, RefObject<HTMLButtonElement>>;
    scrollToSection: (sectionId: string) => void;
    focusSection: (sectionId: string) => void;
}

const ScrollContext = createContext<ScrollContextType | undefined>(undefined);

interface ScrollingProviderProps {
    children: ReactElement[]
    pageName: string
    minOffsetShow: number
    smallDesktopMaxWidth: number
}

const generateRefsDictionary = <T extends HTMLElement>(
    children: ReactElement[]
): Record<string, RefObject<T>> => {
    return children?.reduce((refs, child) => {
        if (child.props.id) {
            refs[child.props.id] = useRef<T>(null);
        }
        return refs;
    }, {} as Record<string, RefObject<T>>);
};

export const ScrollingProvider: React.FC<ScrollingProviderProps> = ({
    children,
    pageName,
    minOffsetShow,
    smallDesktopMaxWidth,
}) => {
    const [activeSection, setActiveSection] = useState<string | null>(null);
    const scrolledChildren = children?.filter(child => child.key && !(child.key?.includes("page"))) || [];
    const sectionRefs = generateRefsDictionary<HTMLDivElement>(scrolledChildren);
    const mobileFooterButonRefs = generateRefsDictionary<HTMLButtonElement>(scrolledChildren);
    const debounceRef = useRef<NodeJS.Timeout | null>(null);


    useEffect(() => {
        if (scrolledChildren.length === 0) {
            return;
        }
    }, [activeSection]);

    useEffect(() => {
        if (!sectionRefs) {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.length === 1) {
                    return
                }
                // Find the entry with the maximum intersection ratio
                const mostVisibleEntry = entries.reduce((prev, curr) =>
                    curr.intersectionRatio > prev.intersectionRatio ? curr : prev
                );

                // Find the entry with the maximum intersection ratio
                const inters = entries.map((item) =>
                    item.intersectionRatio
                );

                // Only update if it’s significantly visible (e.g., 50% or more)
                if (mostVisibleEntry.intersectionRatio >= 0.5) {
                    setActiveSection(mostVisibleEntry.target.id);
                }

                // Clear any pending debounce
                if (debounceRef.current) {
                    clearTimeout(debounceRef.current);
                }
                // Debounce the update: wait 100ms before updating the active section.
                debounceRef.current = setTimeout(() => {
                    // Optional: only update if the candidate is significantly visible (e.g., at least 50%)
                    if (mostVisibleEntry.intersectionRatio >= 0.5 && mostVisibleEntry.target.id !== activeSection) {
                        setActiveSection(mostVisibleEntry.target.id);
                    }
                }, 500); // Adjust delay (in ms) as needed
            },
            {
                threshold: Array.from({ length: 101 }, (_, i) => i / 100),
                // rootMargin: "-25% -5% -30% -36%",
                rootMargin: "-90px 0px 0px 0px",
            },
        )

        Object.keys(sectionRefs).forEach((key) => {
            const curr = sectionRefs[key].current;
            if (curr !== null) {
                observer.observe(curr);
            }
        });
        return () => {
            observer.disconnect();
        };
    }, [sectionRefs]);

    const scrollToSection = (sectionId: string) => {
        const sectionRef = sectionRefs[sectionId];
        if (sectionRef && sectionRef.current) {
            const elementTop = sectionRef.current.getBoundingClientRect().top + window.scrollY;
            window.scrollTo({
                top: elementTop - 90,
                behavior: "smooth",
            });
            setTimeout(() => {
                sectionRef.current?.focus({ preventScroll: true });
            }, 700);
        }
    };

    const focusSection = (sectionId: string) => {
        const sectionRef = sectionRefs[sectionId];
        if (sectionRef && sectionRef.current) {
            sectionRef.current.setAttribute('role', 'tabpanel');
            sectionRef.current.setAttribute('aria-label', sectionId);
            const focusableElements = sectionRef.current.querySelectorAll(
                'a[href], button, textarea, input, select, [tabindex]:not([tabindex="-1"])'
            );
            if (focusableElements.length > 0) {
                (focusableElements[0] as HTMLElement).focus();
            } else {
                sectionRef.current.setAttribute('tabindex', '0');
                sectionRef.current.focus();
            }
        }
    }

    return (
        <ScrollContext.Provider value={{ activeSection, mobileFooterButonRefs, scrollToSection, children: scrolledChildren, focusSection }}>
            <ScrollDesktop minOffsetShow={minOffsetShow} pageName={pageName} smallDesktopMaxWidth={smallDesktopMaxWidth} />
            {children?.map((child) =>
                cloneElement(child, {
                    ref: sectionRefs[child.props.id],
                }),
            )}
        </ScrollContext.Provider>
    );
};

export const useScroll = () => {
    const context = useContext(ScrollContext);
    if (context === undefined) {
        throw new Error("useScroll must be used within a ScrollingProvider");
    }
    return context;
};
